import { useEffect, useState } from "react";
import { formatUpdated } from "./useChainStats";

// Head-to-head: WAX vs Polygon in US dollars. Data from
// netlify/functions/chain-usd-compare.ts (CoinGecko for token price, market
// cap and trading volume; DefiLlama for DeFi TVL and DEX volume).

type Pair = { wax: number | null; polygon: number | null };

interface UsdCompare {
  fetchedAt: number;
  price: Pair;
  marketCap: Pair;
  volume24h: Pair;
  tvl: Pair;
  dexVolume24h: Pair;
}

const REFRESH_MS = 5 * 60 * 1000;

function usd(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(abs >= 1e8 ? 0 : 1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(abs >= 1e5 ? 0 : 1)}K`;
  if (abs >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toPrecision(3)}`;
}

function ratioText(wax: number, polygon: number): string {
  if (wax <= 0 || polygon <= 0) return "";
  const r = wax / polygon;
  if (r >= 1) return r >= 2 ? `WAX ${r.toFixed(1)}× Polygon` : `WAX +${Math.round((r - 1) * 100)}% vs Polygon`;
  if (r >= 0.1) return `WAX = ${Math.round(r * 100)}% of Polygon`;
  const inv = polygon / wax;
  return `Polygon ${inv >= 100 ? Math.round(inv).toLocaleString() : inv.toFixed(1)}× WAX`;
}

function Metric({ title, pair, note }: { title: string; pair: Pair; note: string }) {
  if (pair.wax === null && pair.polygon === null) return null;
  const max = Math.max(pair.wax ?? 0, pair.polygon ?? 0, 1e-12);
  const summary = pair.wax !== null && pair.polygon !== null ? ratioText(pair.wax, pair.polygon) : "";
  const rows: Array<[string, string, number | null]> = [
    ["wax", "WAX", pair.wax],
    ["polygon", "Polygon", pair.polygon],
  ];
  return (
    <div className="wvp__metric">
      <div className="wvp__metric-head">
        <h4 className="wvp__metric-title">{title}</h4>
        {summary && <span className="wvp__ratio">{summary}</span>}
      </div>
      {rows.map(([id, name, value]) => (
        <div key={id} className="chain-compare__row">
          <div className="chain-compare__row-head">
            <span className={`chain-compare__dot chain-compare__dot--${id}`} aria-hidden="true" />
            <span className="chain-compare__label">{name}</span>
            <span className="chain-compare__value">{value === null ? "—" : usd(value)}</span>
          </div>
          <div className="chain-compare__track">
            <div
              className={`chain-compare__bar chain-compare__bar--${id}`}
              style={{ width: `${value === null ? 0 : Math.max((value / max) * 100, 1.5)}%` }}
            />
          </div>
        </div>
      ))}
      <p className="wvp__metric-note">{note}</p>
    </div>
  );
}

export function WaxVsPolygon() {
  const [data, setData] = useState<UsdCompare | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    function load() {
      fetch("/.netlify/functions/chain-usd-compare")
        .then((res) => {
          if (!res.ok) throw new Error(`status ${res.status}`);
          return res.json();
        })
        .then((json: UsdCompare) => {
          if (!cancelled) {
            setData(json);
            setError(false);
          }
        })
        .catch(() => {
          if (!cancelled) setError(true);
        });
    }
    load();
    const id = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!data) {
    return (
      <div className="chain-compare chain-compare--loading" aria-live="polite">
        {error ? "Live WAX vs Polygon data is temporarily unavailable." : "Loading live WAX vs Polygon data…"}
      </div>
    );
  }

  return (
    <div className="chain-compare wvp" role="group" aria-label="WAX versus Polygon in US dollars">
      <div className="chain-compare__header">
        <h3 className="chain-compare__title">WAX vs Polygon · Dollar Value</h3>
        <span className="chain-compare__live">
          <span className="chain-compare__live-dot" aria-hidden="true" />
          Live · updated {formatUpdated(data.fetchedAt)}
        </span>
      </div>

      {(data.price.wax !== null || data.price.polygon !== null) && (
        <div className="wvp__prices">
          {data.price.wax !== null && (
            <span className="wvp__price">
              <span className="chain-compare__dot chain-compare__dot--wax" aria-hidden="true" />
              WAXP <strong>{usd(data.price.wax)}</strong>
            </span>
          )}
          {data.price.polygon !== null && (
            <span className="wvp__price">
              <span className="chain-compare__dot chain-compare__dot--polygon" aria-hidden="true" />
              POL <strong>{usd(data.price.polygon)}</strong>
            </span>
          )}
        </div>
      )}

      <div className="wvp__grid">
        <Metric title="DeFi value locked" pair={data.tvl} note="Dollars deposited in each chain's DeFi apps (TVL)." />
        <Metric title="DEX volume · 24h" pair={data.dexVolume24h} note="Swaps on each chain's decentralized exchanges." />
      </div>

      <p className="chain-compare__note">
        Sources: DefiLlama (DeFi value locked, DEX volume) and CoinGecko (token prices). Refreshed every 15 minutes.
      </p>
    </div>
  );
}
